/**
 * AI Labs section (JAR-3022): partner logo strip, workshop carousel, and CTA.
 * The block cross-fades its bg to the centered workshop's colour; the CTA reuses
 * the page's enrollment CTA.
 */
import { useRef, useState } from 'react'
import { useReducedMotion } from 'motion/react'
import { useTranslation } from '@mc/i18n'
import { Container } from '../Container'
import { InlineCta } from '../InlineCta'
import { PROGRAM_SECTIONS_I18N_NAMESPACE } from '../i18n'
import { CarouselHeader } from '../shared/CarouselHeader'
import { useRowScroll } from '../shared/useRowScroll'
import type {
  AiLabsSectionData,
  ProgramSectionContext,
} from '../types/sections'
import { AiLabsCarousel } from './AiLabsCarousel'
import { AiLabsPartners } from './AiLabsPartners'
import { AI_LABS_WORKSHOPS } from './constants/aiLabsWorkshops'
import type { AiLabsCardItem, AiLabsCarouselHandle, AiLabsTheme } from './types'

// Default bg before the first card settles; `light` mirrors the first workshop
// so an empty row never flashes dark on the parchment surface.
const DEFAULT_BG_COLOR: Record<AiLabsTheme, string> = {
  dark: '#252525',
  light: '#d8d5d1',
}

const CTA_LOCATION = 'ai labs'

interface AiLabsCopy {
  id: string
  title: string
  description?: string
}

interface AiLabsSectionProps extends ProgramSectionContext {
  data: AiLabsSectionData
  /**
   * Set false to drop the enrollment CTA bar. Used by the Status Portal, where
   * the audience is already enrolled and has nothing to convert to.
   */
  withCta?: boolean
  /** Surface the section sits on; picks the panel palette. */
  theme?: AiLabsTheme
  infinite?: boolean
}

export const AiLabsSection = ({
  data,
  withCta = true,
  theme = 'dark',
  infinite = false,
}: AiLabsSectionProps) => {
  const { t } = useTranslation(PROGRAM_SECTIONS_I18N_NAMESPACE)
  const carouselRef = useRef<AiLabsCarouselHandle>(null)
  const prefersReducedMotion = useReducedMotion()
  const [activeIndex, setActiveIndex] = useState(0)
  const { scrollRow } = useRowScroll(carouselRef)

  // Merge copy with media by id; drop copy with no media.
  const rawCopy = t(data.itemsKey, { returnObjects: true })
  const copy = Array.isArray(rawCopy) ? (rawCopy as AiLabsCopy[]) : []
  const items: AiLabsCardItem[] = copy.flatMap((c) => {
    const workshop = AI_LABS_WORKSHOPS[c.id]
    if (!workshop) return []
    const { bgColors, ...media } = workshop
    return [{ ...c, ...media, bgColor: bgColors.dark }]
  })

  const activeId = items[activeIndex]?.id
  const bgColor = activeId
    ? AI_LABS_WORKSHOPS[activeId].bgColors[theme]
    : DEFAULT_BG_COLOR[theme]

  return (
    <section
      id='ai-labs'
      data-section='ai-labs'
      className='tailwind-enabled w-full py-6 text-edu-fg-primary md:py-12'
    >
      <Container bleed>
        {/* Cross-fade bg to active workshop colour (skip if reduced motion). */}
        <div
          className={`flex flex-col px-6 py-12 md:px-25 md:py-16 ${
            prefersReducedMotion
              ? ''
              : 'transition-colors duration-500 ease-in-out'
          }`}
          style={{ backgroundColor: bgColor }}
        >
          <AiLabsPartners
            headline={t(data.partnersKey ?? 'aiLabs.partnersHeadline')}
          />

          <div className='mt-10 flex flex-col gap-6 md:mt-12 md:gap-4'>
            <CarouselHeader
              title={t('aiLabs.headline')}
              prevLabel={t('aiLabs.previousLabel')}
              nextLabel={t('aiLabs.nextLabel')}
              onNav={scrollRow}
              dividerClassName='border-edu-fg-dimmed'
            />

            <AiLabsCarousel
              ref={carouselRef}
              items={items}
              infinite={infinite}
              sectionLabel={t('aiLabs.sectionLabel')}
              onActiveIndexChange={setActiveIndex}
            />
          </div>

          {/* The bottom margin only exists to separate the disclaimer from the CTA. */}
          <p
            className={`m-0 mt-6 text-mono-2 text-edu-fg-semi-dimmed ${
              withCta ? 'mb-16' : ''
            }`}
          >
            {t('aiLabs.disclaimer')}
          </p>

          {/* Copy differs per breakpoint; the bar itself is the shared one. */}
          {withCta && (
            <InlineCta
              headline={t('aiLabs.cta.text')}
              mobileHeadline={t('aiLabs.cta.textMobile')}
              ctaLocation={CTA_LOCATION}
            />
          )}
        </div>
      </Container>
    </section>
  )
}
